import EmptyState from '@/components/EmptyState';
import {
  useAdjustGiftCard,
  useGiftCard,
  useVoidGiftCard,
} from '@/features/gift-cards/hooks/useGiftCards';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { formatMoney, inputToMinor } from '@/utils/money';
import {
  Badge,
  Block,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useState } from 'react';

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'green',
  DEPLETED: 'gray',
  VOID: 'red',
  EXPIRED: 'orange',
};
const TXN_LABEL: Record<string, string> = {
  ISSUE: 'Issued',
  REDEEM: 'Redeemed',
  ADJUST: 'Adjusted',
  VOID: 'Voided',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function GiftCardDetailPage({ f7route }: { f7route: { params: { giftCardId: string } } }) {
  const giftCardId = f7route.params.giftCardId;
  const { data: card, isLoading, error } = useGiftCard(giftCardId);
  const voidCard = useVoidGiftCard();
  const adjust = useAdjustGiftCard();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';

  const [adjusting, setAdjusting] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');

  const confirmVoid = () => {
    if (!card) return;
    f7.dialog.confirm(
      `Void ${card.code}? The remaining balance is forfeited and the card can’t be used.`,
      'Void gift card',
      () => voidCard.mutate({ giftCardId, reason: 'Voided by staff' }),
    );
  };

  const applyAdjust = () => {
    const cents =
      inputToMinor(adjustAmount.replace('-', ''), currency) *
      (adjustAmount.trim().startsWith('-') ? -1 : 1);
    if (!Number.isInteger(cents) || cents === 0) {
      f7.toast.show({
        text: 'Enter a non-zero amount (prefix − to deduct).',
        closeTimeout: 2500,
        position: 'bottom',
      });
      return;
    }
    if (adjustReason.trim().length === 0) {
      f7.toast.show({ text: 'A reason is required.', closeTimeout: 2500, position: 'bottom' });
      return;
    }
    adjust.mutate(
      { giftCardId, amountCents: cents, reason: adjustReason.trim() },
      {
        onSuccess: () => {
          f7.toast.show({ text: 'Balance adjusted', closeTimeout: 2000, position: 'bottom' });
          setAdjusting(false);
          setAdjustAmount('');
          setAdjustReason('');
        },
      },
    );
  };

  return (
    <Page name="gift-card-detail">
      <Navbar title="Gift card" backLink="Back" />

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title="Couldn’t load card"
          description={(error as Error).message}
        />
      )}

      {card && (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListItem title="Code" after={card.code} />
            <ListItem title="Balance" after={formatMoney(card.balanceCents, card.currency)} />
            <ListItem
              title="Initial"
              after={formatMoney(card.initialBalanceCents, card.currency)}
            />
            <ListItem title="Status">
              <div slot="after">
                <Badge color={STATUS_COLOR[card.status] ?? 'gray'}>{card.status}</Badge>
              </div>
            </ListItem>
            {card.recipientName && <ListItem title="Recipient" after={card.recipientName} />}
          </List>

          {card.status !== 'VOID' && (
            <Block className="grid grid-cols-2 grid-gap">
              <Button outline onClick={() => setAdjusting((v) => !v)}>
                Adjust balance
              </Button>
              <Button outline color="red" onClick={confirmVoid} disabled={voidCard.isPending}>
                Void card
              </Button>
            </Block>
          )}

          {adjusting && (
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              <ListInput
                label={`Adjustment (${currency}, prefix − to deduct)`}
                type="text"
                placeholder="10.00 or -5.00"
                value={adjustAmount}
                onInput={(e) => setAdjustAmount(e.target.value)}
              />
              <ListInput
                label="Reason"
                type="text"
                placeholder="Goodwill / correction"
                value={adjustReason}
                onInput={(e) => setAdjustReason(e.target.value)}
              />
              <ListItem>
                <Button slot="title" fill onClick={applyAdjust} disabled={adjust.isPending}>
                  {adjust.isPending ? 'Applying…' : 'Apply'}
                </Button>
              </ListItem>
            </List>
          )}

          <BlockTitle>History</BlockTitle>
          <List strongIos strongMd outlineIos dividersIos dividersMd>
            {card.transactions.map((t) => (
              <ListItem
                key={t.id}
                title={`${TXN_LABEL[t.type] ?? t.type}${t.reason ? ` · ${t.reason}` : ''}`}
                footer={fmtDate(t.createdAt)}
                after={`${t.amountCents > 0 ? '+' : ''}${formatMoney(t.amountCents, card.currency)}`}
              />
            ))}
          </List>
        </>
      )}
    </Page>
  );
}

export default GiftCardDetailPage;
